<script setup>
import { ref, onMounted, watch } from 'vue'
import axios from 'axios'
import Header from './component/header.vue'

const loading   = ref(false)
const saving    = ref(false)
const cycles    = ref([])
const formulas  = ref([])
const rules     = ref([])
const payouts   = ref({ data: [], current_page: 1, last_page: 1 })
const selCycle  = ref('')
const tab       = ref('formulas')
const showFormulaModal = ref(false)
const showRuleModal    = ref(false)
const formError = ref('')
const page      = ref(1)

const blankFormula = () => ({ name: '', formula_type: 'percentage_of_salary', rules: [{ min_score: 0, max_score: 100, value: 0 }] })
const blankRule    = () => ({ name: '', score_threshold: 0, score_threshold_max: null, operator: 'gte', adjustment_type: 'percentage', adjustment_value: 0 })
const fForm = ref(blankFormula())
const rForm = ref(blankRule())

const loadCycles = async () => {
  const res = await axios.get('/hr/pms/cycles', { params: { per_page: 100 } })
  cycles.value = res.data.data?.data ?? []
}

const loadFormulas = async () => {
  const res = await axios.get('/hr/pms/bonus/formulas', { params: { cycle_id: selCycle.value || undefined } })
  formulas.value = res.data.data ?? []
}

const loadRules = async () => {
  const res = await axios.get('/hr/pms/bonus/salary-rules', { params: { cycle_id: selCycle.value || undefined } })
  rules.value = res.data.data ?? []
}

const loadPayouts = async () => {
  if (!selCycle.value) { payouts.value = { data: [], current_page: 1, last_page: 1 }; return }
  try {
    loading.value = true
    const res = await axios.get(`/hr/pms/bonus/cycles/${selCycle.value}/payouts`, { params: { per_page: 20, page: page.value } })
    payouts.value = res.data.data
  } catch (e) { console.error(e) } finally { loading.value = false }
}

const calculate = async () => {
  if (!selCycle.value) { alert('Select a cycle first'); return }
  if (!confirm('Calculate payouts for this cycle? This will overwrite existing calculated payouts.')) return
  try {
    saving.value = true
    await axios.post(`/hr/pms/bonus/cycles/${selCycle.value}/calculate`)
    loadPayouts()
  } catch (e) { alert(e.response?.data?.message ?? 'Failed to calculate') } finally { saving.value = false }
}

const approvePayouts = async () => {
  if (!selCycle.value) return
  if (!confirm('Approve all calculated payouts?')) return
  try {
    saving.value = true
    await axios.post(`/hr/pms/bonus/cycles/${selCycle.value}/approve`)
    loadPayouts()
  } catch (e) { alert(e.response?.data?.message ?? 'Failed') } finally { saving.value = false }
}

const saveFormula = async () => {
  try {
    saving.value = true
    formError.value = ''
    await axios.post('/hr/pms/bonus/formulas', { ...fForm.value, cycle_id: selCycle.value || null })
    showFormulaModal.value = false
    loadFormulas()
  } catch (e) { formError.value = e.response?.data?.message ?? 'Failed' } finally { saving.value = false }
}

const saveRule = async () => {
  try {
    saving.value = true
    formError.value = ''
    await axios.post('/hr/pms/bonus/salary-rules', { ...rForm.value, cycle_id: selCycle.value || null })
    showRuleModal.value = false
    loadRules()
  } catch (e) { formError.value = e.response?.data?.message ?? 'Failed' } finally { saving.value = false }
}

const addFormulaRow = () => fForm.value.rules.push({ min_score: 0, max_score: 100, value: 0 })
const removeFormulaRow = (i) => fForm.value.rules.splice(i, 1)

onMounted(() => { loadCycles(); loadFormulas(); loadRules() })
watch(selCycle, () => { loadFormulas(); loadRules(); loadPayouts() })
watch(page, loadPayouts)

const fmt = (v) => Number(v ?? 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })
</script>

<template>
  <div>
    <Header />
    <div class="pms-page">
      <div class="pms-toolbar">
        <div class="pms-tabs">
          <button :class="['be-tab', tab === 'formulas' ? 'be-tab--active' : '']" @click="tab = 'formulas'">Bonus Formulas</button>
          <button :class="['be-tab', tab === 'rules' ? 'be-tab--active' : '']" @click="tab = 'rules'">Salary Rules</button>
          <button :class="['be-tab', tab === 'payouts' ? 'be-tab--active' : '']" @click="tab = 'payouts'">Payouts</button>
        </div>
        <select v-model="selCycle" class="pms-select">
          <option value="">All Cycles</option>
          <option v-for="c in cycles" :key="c.id" :value="c.id">{{ c.name }}</option>
        </select>
      </div>

      <!-- Formulas tab -->
      <template v-if="tab === 'formulas'">
        <div class="tab-actions">
          <button class="btn-primary" @click="() => { fForm = blankFormula(); showFormulaModal = true }">+ New Formula</button>
        </div>
        <div class="card-grid">
          <div v-if="!formulas.length" class="empty-state">No formulas configured yet.</div>
          <div v-for="f in formulas" :key="f.id" class="be-card">
            <div class="be-card-name">{{ f.name }}</div>
            <div class="be-card-type">{{ f.formula_type.replace(/_/g,' ') }}</div>
            <div class="be-tiers">
              <div v-for="(r, i) in (f.rules ?? [])" :key="i" class="be-tier">
                Score {{ r.min_score }}–{{ r.max_score }} → <strong>{{ r.value }}{{ f.formula_type === 'fixed_amount' ? ' ₦' : '%' }}</strong>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Salary rules tab -->
      <template v-else-if="tab === 'rules'">
        <div class="tab-actions">
          <button class="btn-primary" @click="() => { rForm = blankRule(); showRuleModal = true }">+ New Rule</button>
        </div>
        <div class="table-card">
          <table class="data-table">
            <thead><tr><th>Name</th><th>Operator</th><th>Threshold</th><th>Adjustment</th></tr></thead>
            <tbody>
              <tr v-if="!rules.length"><td colspan="4" class="tbl-center">No rules configured</td></tr>
              <tr v-for="r in rules" :key="r.id">
                <td>{{ r.name ?? '—' }}</td>
                <td>{{ r.operator }}</td>
                <td>{{ r.score_threshold }}{{ r.operator === 'between' ? ' – ' + r.score_threshold_max : '' }}</td>
                <td>{{ r.adjustment_value }}{{ r.adjustment_type === 'percentage' ? '%' : ' ₦' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <!-- Payouts tab -->
      <template v-else>
        <div class="tab-actions">
          <button class="btn-outline" :disabled="saving || !selCycle" @click="calculate">Calculate Payouts</button>
          <button class="btn-primary" :disabled="saving || !selCycle" @click="approvePayouts">Approve All</button>
        </div>
        <div class="table-card">
          <div v-if="loading" class="tbl-center">Loading…</div>
          <table v-else class="data-table">
            <thead><tr><th>Employee</th><th>Score</th><th>Rating Band</th><th>Bonus</th><th>Salary Adj.</th><th>Status</th></tr></thead>
            <tbody>
              <tr v-if="!payouts.data?.length"><td colspan="6" class="tbl-center">No payouts yet. Run Calculate first.</td></tr>
              <tr v-for="p in payouts.data" :key="p.id">
                <td>{{ p.employee?.first_name }} {{ p.employee?.last_name }}</td>
                <td>{{ p.final_score?.toFixed(1) ?? '—' }}</td>
                <td>{{ p.rating_band ?? '—' }}</td>
                <td>₦{{ fmt(p.bonus_amount) }}</td>
                <td>₦{{ fmt(p.salary_adjustment) }}</td>
                <td><span :class="['badge', p.status === 'synced' ? 'badge--green' : p.status === 'approved' ? 'badge--blue' : 'badge--grey']">{{ p.status }}</span></td>
              </tr>
            </tbody>
          </table>
          <div v-if="payouts.last_page > 1" class="pagination">
            <button :disabled="page <= 1" @click="page--">‹</button>
            <span>{{ page }} / {{ payouts.last_page }}</span>
            <button :disabled="page >= payouts.last_page" @click="page++">›</button>
          </div>
        </div>
      </template>
    </div>

    <!-- Formula Modal -->
    <div v-if="showFormulaModal" class="modal-overlay" @click.self="showFormulaModal = false">
      <div class="modal-box">
        <div class="modal-header">
          <h3>New Bonus Formula</h3>
          <button class="modal-close" @click="showFormulaModal = false">×</button>
        </div>
        <div class="modal-body">
          <div v-if="formError" class="form-error">{{ formError }}</div>
          <div class="form-grid">
            <div class="form-group form-group--full">
              <label>Name</label>
              <input v-model="fForm.name" placeholder="e.g. Annual Bonus 2026" />
            </div>
            <div class="form-group form-group--full">
              <label>Formula Type</label>
              <select v-model="fForm.formula_type">
                <option value="percentage_of_salary">% of Salary</option>
                <option value="fixed_amount">Fixed Amount</option>
                <option value="tiered">Tiered</option>
              </select>
            </div>
          </div>
          <div class="rules-editor">
            <div class="rules-header">
              <span class="rules-title">Rules (Score → Bonus)</span>
              <button class="btn-link" @click="addFormulaRow">+ Add Row</button>
            </div>
            <div v-for="(r, i) in fForm.rules" :key="i" class="rule-row">
              <input type="number" v-model.number="r.min_score" placeholder="Min score" />
              <span>–</span>
              <input type="number" v-model.number="r.max_score" placeholder="Max score" />
              <span>→</span>
              <input type="number" v-model.number="r.value" placeholder="Value" />
              <button class="btn-link btn-link--red" @click="removeFormulaRow(i)">×</button>
            </div>
          </div>
          <div class="modal-actions">
            <button class="btn-outline" @click="showFormulaModal = false">Cancel</button>
            <button class="btn-primary" :disabled="saving" @click="saveFormula">Save Formula</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Salary Rule Modal -->
    <div v-if="showRuleModal" class="modal-overlay" @click.self="showRuleModal = false">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <h3>New Salary Rule</h3>
          <button class="modal-close" @click="showRuleModal = false">×</button>
        </div>
        <div class="modal-body">
          <div v-if="formError" class="form-error">{{ formError }}</div>
          <div class="form-grid">
            <div class="form-group form-group--full"><label>Name</label><input v-model="rForm.name" /></div>
            <div class="form-group"><label>Operator</label>
              <select v-model="rForm.operator">
                <option value="gte">≥ (gte)</option>
                <option value="lte">≤ (lte)</option>
                <option value="eq">= (eq)</option>
                <option value="between">Between</option>
              </select>
            </div>
            <div class="form-group"><label>Score Threshold</label><input type="number" v-model.number="rForm.score_threshold" /></div>
            <div v-if="rForm.operator === 'between'" class="form-group"><label>Max Threshold</label><input type="number" v-model.number="rForm.score_threshold_max" /></div>
            <div class="form-group"><label>Adjustment Type</label>
              <select v-model="rForm.adjustment_type"><option value="percentage">Percentage</option><option value="fixed">Fixed Amount</option></select>
            </div>
            <div class="form-group"><label>Value</label><input type="number" v-model.number="rForm.adjustment_value" /></div>
          </div>
          <div class="modal-actions">
            <button class="btn-outline" @click="showRuleModal = false">Cancel</button>
            <button class="btn-primary" :disabled="saving" @click="saveRule">Save Rule</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.pms-page { padding: 0 24px 40px; }
.pms-toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; flex-wrap: wrap; gap: 10px; }
.pms-tabs  { display: flex; gap: 4px; }
.be-tab    { padding: 7px 14px; border: 1px solid #dde3ef; border-radius: 6px; background: #fff; font-size: 13px; cursor: pointer; color: #555; }
.be-tab--active { background: #004AAD; color: #fff; border-color: #004AAD; }
.pms-select { padding: 8px 12px; border: 1px solid #dde3ef; border-radius: 6px; font-size: 13px; }
.tab-actions { display: flex; gap: 10px; margin-bottom: 14px; }
.card-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 16px; }
.be-card  { background: #fff; border: 1px solid #eaf0fb; border-radius: 10px; padding: 18px 20px; }
.be-card-name { font-size: 15px; font-weight: 700; color: #1a2a45; margin-bottom: 4px; }
.be-card-type { font-size: 12px; color: #888; text-transform: capitalize; margin-bottom: 12px; }
.be-tiers { display: flex; flex-direction: column; gap: 4px; }
.be-tier  { font-size: 13px; color: #445; }
.empty-state { color: #999; font-size: 14px; padding: 40px; text-align: center; }
.table-card { background: #fff; border-radius: 10px; border: 1px solid #eaf0fb; overflow: hidden; }
.data-table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.data-table th { padding: 12px 14px; text-align: left; background: #f7f9fc; font-weight: 600; color: #556; font-size: 12px; }
.data-table td { padding: 12px 14px; border-top: 1px solid #f0f3fa; }
.tbl-center { text-align: center; color: #999; padding: 40px; }
.pagination { display: flex; align-items: center; gap: 12px; justify-content: center; padding: 12px; }
.pagination button { padding: 5px 12px; border: 1px solid #dde3ef; border-radius: 4px; cursor: pointer; background: #fff; }
.pagination button:disabled { opacity: 0.4; }
.badge { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 11.5px; font-weight: 600; text-transform: capitalize; }
.badge--grey  { background: #f0f3fa; color: #556; }
.badge--blue  { background: #eef4ff; color: #004AAD; }
.badge--green { background: #d1e7dd; color: #0f5132; }
.rules-editor { margin: 16px 0; }
.rules-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.rules-title  { font-size: 13px; font-weight: 600; color: #556; }
.rule-row { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.rule-row input { padding: 7px 10px; border: 1px solid #dde3ef; border-radius: 6px; font-size: 13px; width: 80px; }
.btn-primary { padding: 9px 20px; background: #004AAD; color: #fff; border: none; border-radius: 6px; font-size: 13.5px; font-weight: 600; cursor: pointer; }
.btn-outline { padding: 9px 20px; background: #fff; color: #004AAD; border: 1px solid #004AAD; border-radius: 6px; font-size: 13.5px; cursor: pointer; }
.btn-link { background: none; border: none; color: #004AAD; font-size: 13px; cursor: pointer; }
.btn-link--red { color: #dc3545; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 1000; display: flex; align-items: flex-start; justify-content: center; padding-top: 60px; }
.modal-box { background: #fff; border-radius: 12px; width: 580px; max-width: 95vw; max-height: 85vh; overflow-y: auto; box-shadow: 0 8px 40px rgba(0,0,0,0.18); }
.modal-box--sm { width: 420px; }
.modal-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px 16px; border-bottom: 1px solid #eaf0fb; }
.modal-header h3 { font-size: 16px; font-weight: 700; color: #1a2a45; margin: 0; }
.modal-close { background: none; border: none; font-size: 22px; color: #888; cursor: pointer; }
.modal-body { padding: 20px 24px 24px; }
.modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px; }
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 18px; }
.form-group { display: flex; flex-direction: column; gap: 5px; }
.form-group--full { grid-column: 1 / -1; }
.form-group label { font-size: 12.5px; font-weight: 600; color: #556; }
.form-group input, .form-group select { padding: 9px 12px; border: 1px solid #dde3ef; border-radius: 6px; font-size: 13.5px; color: #1a2a45; }
.form-error { background: #f8d7da; color: #842029; padding: 10px 14px; border-radius: 6px; font-size: 13px; margin-bottom: 14px; }
</style>
